/**
 * This is utility class used to provide utility functions that can be used with ImmutableJS
 *
 */
import Immutable, { List, Map } from "immutable";

var ImmutableUtils = {
  /** Uses this function with filter to "pick" value **/
  keyIn: (...args) => {
    const isSingleArray = args.length === 1 && Array.isArray(args[0]);
    const argumentsArray = isSingleArray ? args[0] : args;

    const keySet = Immutable.Set(argumentsArray);
    return (v, k) => {
      return keySet.has(k);
    };
  },

  not: (predicate) => {
    return (...args) => {
      return !predicate.apply(this, args);
    };
  },
};

/**
 * @typedef {string} id
 */

/**
 * @typedef {number} index
 */

/**
 * @typedef OrderedMapItem
 * @prop {id} id
 * @prop {index} index
 */

/**
 * @typedef {Map<id, OrderedMapItem>} OrderedMap
 */

/**
 * Orders map by index value, returns list of ordered keys
 * @param {OrderedMap} orderedMap
 * @returns {List<id>}
 *

/**
 * @typedef PossiblyOrderedMapItem
 * @prop {id=} id
 * @prop {index=} index
 */

export const getOrderedMapIds = (orderedMap) =>
  List([...orderedMap.sortBy((item) => item.get("order")).keys()]);

/**
 * Reorders OrderedMap entries based on provided order list
 * @param {List<id>} orderList
 * @param {OrderedMap} orderedMap
 * @returns {OrderedMap}
 */
export const reOrderMapIds = (orderList, orderedMap) =>
  orderedMap.map((item) => {
    const id = item.get("id");
    return item.set("order", orderList.indexOf(id));
  });

/**
 * Verifies shape of ImmutableMap as OrderedMap
 * @param {Map<id, PossiblyOrderedMapItem>} possiblyOrderedMap
 * @returns {boolean}
 */
export const isOrderedMap = (possiblyOrderedMap) =>
  Map.isMap(possiblyOrderedMap) &&
  possiblyOrderedMap.every((item) => item.has("id") && item.has("order"));

export const OrderedMap = {
  getIds: getOrderedMapIds,
  reorder: reOrderMapIds,
  isOrderedMap: isOrderedMap,
};

/**
 * Returns elements from Immutable maps or lists based on provided paths, great for destructuring immutable data
 * @param {Map|List} mapOrList - Immutable map or list
 * @param {string[]|List} paths - array of paths to get from map
 * @param {*} [defaultValue]  - default value to return if path is not found
 * @returns {array}
 */
export const getFromImmutable = (mapOrList, paths = [], defaultValue) => {
  const elements = [];

  if (!mapOrList || (!Map.isMap(mapOrList) && !List.isList(mapOrList))) {
    console.warn("getFromImmutable: mapOrList is not an Immutable Map or List");
    return elements;
  }

  paths.forEach((path) => {
    elements.push(
      typeof path !== "object"
        ? mapOrList.get(path, defaultValue)
        : mapOrList.getIn(path, defaultValue),
    );
  });
  return elements;
};

export default ImmutableUtils;
